import { createContext, useContext, useState, useRef, useCallback, useEffect, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { connectSupportSocket, type ChatSessionSummary } from '@/api/multichannel'

export type MessageStatus = 'sent' | 'delivered' | 'seen'

export interface LiveMessage {
  id: string
  chatId: string
  senderId: string
  content: string
  sentAt: string
  mine: boolean
  status?: MessageStatus
}

interface SupportChatContextValue {
  connected: boolean
  liveByChat: Record<string, LiveMessage[]>
  sendMessage: (chatId: string | undefined, content: string) => void
  markSeen: (chatId: string | undefined) => void
  pruneChat: (chatId: string, confirmedIds: Set<string>) => void
}

const SupportChatContext = createContext<SupportChatContextValue | null>(null)

// Single WebSocket connection shared by every staff-facing chat surface
// (the full Live Chat inbox page and the global floating bubble). Each
// connection registers the agent into multichannel's round-robin support
// pool server-side — opening a second one from the same browser tab would
// double-register the same agent and skew assignment, so this must be the
// only place that calls connectSupportSocket().
export function SupportChatProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [connected, setConnected] = useState(false)
  const [liveByChat, setLiveByChat] = useState<Record<string, LiveMessage[]>>({})
  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closedByUsRef = useRef(false)

  const connect = useCallback(() => {
    const socket = connectSupportSocket()
    if (!socket) return
    socketRef.current = socket

    socket.onopen = () => setConnected(true)

    socket.onmessage = event => {
      let msg: { Type?: string; sender_id?: string; recipient_id?: string; content?: string; chat_id?: string; id?: string; direction?: string }
      try {
        msg = JSON.parse(event.data)
      } catch {
        return
      }

      if (msg.Type === 'delivered' && msg.chat_id && msg.id) {
        setLiveByChat(prev => ({
          ...prev,
          [msg.chat_id!]: (prev[msg.chat_id!] ?? []).map(m =>
            m.id === msg.id && m.status !== 'seen' ? { ...m, status: 'delivered' } : m),
        }))
        return
      }

      if (msg.Type === 'seen' && msg.chat_id) {
        setLiveByChat(prev => ({
          ...prev,
          [msg.chat_id!]: (prev[msg.chat_id!] ?? []).map(m => m.mine ? { ...m, status: 'seen' } : m),
        }))
        return
      }

      if (msg.Type === 'sla_breach') {
        // Pushed by multichannel's breach ticker — refresh the inbox list
        // immediately so the SLA badge shows up without waiting for the
        // next 15s poll.
        qc.invalidateQueries({ queryKey: ['multichannel-sessions'] })
        return
      }

      if (msg.Type !== 'text' || !msg.chat_id || !msg.content) return

      if (msg.direction === 'outgoing') {
        // Echo of our own message with its real, server-assigned id — adopt
        // it onto the oldest still-unconfirmed local copy in this chat so
        // later delivered/seen events (keyed by that real id) can find it.
        if (!msg.id) return
        setLiveByChat(prev => {
          const list = prev[msg.chat_id!] ?? []
          const idx = list.findIndex(m => m.id.startsWith('local-'))
          if (idx === -1) return prev
          const updated = [...list]
          updated[idx] = { ...updated[idx], id: msg.id! }
          return { ...prev, [msg.chat_id!]: updated }
        })
        return
      }

      setLiveByChat(prev => ({
        ...prev,
        [msg.chat_id!]: [
          ...(prev[msg.chat_id!] ?? []),
          {
            id: msg.id ?? `${Date.now()}-${Math.random()}`,
            chatId: msg.chat_id!,
            senderId: msg.sender_id ?? '',
            content: msg.content!,
            sentAt: new Date().toISOString(),
            mine: false,
          },
        ],
      }))
      // Refreshes unread counts/last-message preview for both the inbox list
      // and the bubble's badge, which both read from this same query.
      qc.invalidateQueries({ queryKey: ['multichannel-sessions'] })
    }

    socket.onclose = () => {
      setConnected(false)
      socketRef.current = null
      if (!closedByUsRef.current) {
        reconnectTimerRef.current = setTimeout(connect, 3_000)
      }
    }
    socket.onerror = () => socket.close()
  }, [qc])

  useEffect(() => {
    closedByUsRef.current = false
    connect()
    return () => {
      closedByUsRef.current = true
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      socketRef.current?.close()
      socketRef.current = null
    }
  }, [connect])

  const sendMessage = useCallback((chatId: string | undefined, content: string) => {
    const text = content.trim()
    const socket = socketRef.current
    if (!text || !socket || socket.readyState !== WebSocket.OPEN) return

    socket.send(JSON.stringify({
      Type: 'text',
      recipient_id: 'support',
      content: text,
      ...(chatId ? { chat_id: chatId } : {}),
    }))

    setLiveByChat(prev => ({
      ...prev,
      [chatId ?? '__pending__']: [
        ...(prev[chatId ?? '__pending__'] ?? []),
        { id: `local-${Date.now()}`, chatId: chatId ?? '', content: text, senderId: 'me', sentAt: new Date().toISOString(), mine: true, status: 'sent' },
      ],
    }))
  }, [])

  const markSeen = useCallback((chatId: string | undefined) => {
    if (!chatId) return

    qc.setQueriesData<ChatSessionSummary[]>({ queryKey: ['multichannel-sessions'] }, sessions => {
      if (!sessions) return sessions
      let changed = false
      const next = sessions.map(session => {
        if (session.chat_id !== chatId || !session.unread_count) return session
        changed = true
        return { ...session, unread_count: 0 }
      })
      return changed ? next : sessions
    })

    const socket = socketRef.current
    if (!socket || socket.readyState !== WebSocket.OPEN) return
    socket.send(JSON.stringify({ Type: 'seen', chat_id: chatId }))
  }, [qc])

  // Once history confirms a message is persisted, drop it from liveByChat so it
  // doesn't duplicate when combined with the REST history response.
  const pruneChat = useCallback((chatId: string, confirmedIds: Set<string>) => {
    setLiveByChat(prev => {
      const list = prev[chatId]
      if (!list) return prev
      const pruned = list.filter(m => m.id.startsWith('local-') || !confirmedIds.has(m.id))
      if (pruned.length === list.length) return prev
      return { ...prev, [chatId]: pruned }
    })
  }, [])

  return (
    <SupportChatContext.Provider value={{ connected, liveByChat, sendMessage, markSeen, pruneChat }}>
      {children}
    </SupportChatContext.Provider>
  )
}

export function useSupportChat() {
  const ctx = useContext(SupportChatContext)
  if (!ctx) throw new Error('useSupportChat must be used within SupportChatProvider')
  return ctx
}
